import { useTabHighlights } from "../../SourceDataPanel/useTabHighlights";
import styles from "./SegmentedToggle.module.css";

export type SegmentedOption<Value extends string> = {
  value: Value;
  text: string;
  ariaLabel?: string;
  disabled?: boolean;
  title?: string;
};

type SegmentedToggleProps<Value extends string> = {
  name: string;
  ariaLabel: string;
  value: Value;
  options: SegmentedOption<Value>[];
  onChange: (value: Value) => void;
  size?: "small";
  fill?: boolean;
  disabled?: boolean;
  className?: string;
};

/**
 * A row of radios drawn as one track with a single pill that slides to the picked option; arrow keys
 * move between options. The pill is placed from the option labels' measured boxes, and the labels never move.
 * `fill` stretches the track to its container and gives every option an equal share.
 */
export function SegmentedToggle<Value extends string>({
  name,
  ariaLabel,
  value,
  options,
  onChange,
  size,
  fill = false,
  disabled = false,
  className,
}: SegmentedToggleProps<Value>) {
  const highlights = useTabHighlights(
    options.map(option => option.value),
    value,
  );
  const classes = [
    styles.toggle,
    size === "small" ? styles.small : "",
    fill ? styles.fill : "",
    disabled ? styles.disabled : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      ref={highlights.registerList}
      role="radiogroup"
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
      className={classes}
    >
      <span
        aria-hidden="true"
        className={styles.pill}
        data-pill=""
        data-instant={highlights.selectedHighlight.instant ? "" : undefined}
        style={highlights.selectedHighlight.style}
      />
      {options.map(option => (
        <label
          key={option.value}
          ref={element => highlights.registerTab(option.value, element)}
          className={styles.option}
          title={option.title}
        >
          <input
            type="radio"
            className={styles.input}
            name={name}
            value={option.value}
            checked={option.value === value}
            disabled={disabled || option.disabled}
            aria-label={option.ariaLabel}
            onChange={() => onChange(option.value)}
          />
          <span className={styles.text} data-text={option.text}>
            {option.text}
          </span>
        </label>
      ))}
    </div>
  );
}
